import { useEffect, useState } from 'react';
import { ApiError, seerrApi, type MediaStatus, type SeerrMedia } from '../api';
import { releaseLabel, releaseOf, requestStatusOf, setRequestStatus, useRequestStatuses } from '../requests';

const whenLabel = (rel: string) => (rel === 'pending' ? '⚡ zodra digitaal uit' : `📅 ${releaseLabel(rel)}`);

export const STATUS_LABEL: Record<MediaStatus, string> = {
  none: '',
  requested: 'Aangevraagd',
  processing: 'Bezig',
  partial: 'Deels beschikbaar',
  available: 'Beschikbaar',
};

/** Poster met status en aanvraagknop. Bij series opent een seizoenkeuze. */
export function RequestCard({ m, canRequest, onRequested }: { m: SeerrMedia; canRequest: boolean; onRequested?: () => void }) {
  useRequestStatuses();
  const [localStatus, setStatus] = useState<MediaStatus>(m.status);
  // Wat we weten uit Seerr (of net zelf hebben aangevraagd) gaat voor
  const shared = requestStatusOf(m.mediaType, m.tmdbId);
  const status: MediaStatus = localStatus !== 'none' ? localStatus : shared ?? 'none';
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [profiles, setProfiles] = useState<{ list: { id: number; name: string }[]; suggested?: number } | null>(null);

  const requestable = !waiting && canRequest && (status === 'none' || (m.mediaType === 'tv' && status === 'partial'));

  /** Film: kun je een kwaliteitsprofiel kiezen (bijv. "UHD Dutch"), dan eerst die keuze; anders meteen aanvragen. */
  const startMovie = async () => {
    setBusy(true);
    const r = await seerrApi.movieProfiles(m.tmdbId).catch(() => ({ profiles: [] as { id: number; name: string }[], suggested: undefined }));
    setBusy(false);
    if (r.profiles.length > 1) setProfiles({ list: r.profiles, suggested: r.suggested });
    else void doRequest();
  };

  const doRequest = async (seasons?: number[], profileId?: number) => {
    setBusy(true);
    setError(null);
    try {
      const r = await seerrApi.request(m.mediaType, m.tmdbId, seasons, { title: m.title, poster: m.poster, profileId });
      setProfiles(null);
      if (r.profileApplied === false) setError('Aangevraagd, maar Seerr gebruikte het standaardprofiel (geef deze gebruiker in Seerr het recht "Advanced Requests").');
      if (r.pending) {
        setWaiting(true);
      } else {
        setStatus('requested');
        setRequestStatus(m.mediaType, m.tmdbId, 'requested');
      }
      setPicking(false);
      onRequested?.();
    } catch (err) {
      setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Aanvragen mislukt.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="req-card">
      <div className="req-poster" style={m.poster ? { backgroundImage: `url("${m.poster}")` } : undefined}>
        {!m.poster && <span className="card-fallback">{m.title}</span>}
        {waiting && status === 'none' && <span className="req-status st-requested">Wacht op goedkeuring</span>}
        {status !== 'none' && (() => {
          const rel = status === 'requested' || status === 'processing' ? releaseOf(m.mediaType, m.tmdbId) : null;
          return (
            <span className={`req-status st-${status}`}>
              {rel ? 'Aangevraagd' : STATUS_LABEL[status]}
              {rel && <span className="lib-badge-date"> · {whenLabel(rel)}</span>}
            </span>
          );
        })()}
      </div>
      <div className="req-body">
        <h3 className="card-title">{m.title}</h3>
        <span className="card-sub">{[m.mediaType === 'tv' ? 'Serie' : 'Film', m.year].filter(Boolean).join(' · ')}</span>
        {requestable && (
          <button className="btn btn-sm" disabled={busy} onClick={() => (m.mediaType === 'tv' ? setPicking(true) : void startMovie())}>
            {busy ? 'Bezig…' : status === 'partial' ? 'Meer aanvragen' : 'Aanvragen'}
          </button>
        )}
        {error && <span className="error req-error" role="alert">{error}</span>}
      </div>
      {picking && <SeasonPicker m={m} busy={busy} onCancel={() => setPicking(false)} onConfirm={(s) => void doRequest(s)} />}
      {profiles && <ProfilePicker m={m} profiles={profiles.list} suggested={profiles.suggested} busy={busy} onCancel={() => setProfiles(null)} onConfirm={(id) => void doRequest(undefined, id)} />}
    </article>
  );
}

function SeasonPicker({ m, busy, onCancel, onConfirm }: { m: SeerrMedia; busy: boolean; onCancel: () => void; onConfirm: (seasons: number[]) => void }) {
  const [seasons, setSeasons] = useState<{ number: number; name: string; episodes: number; status: MediaStatus }[] | null>(null);
  const [chosen, setChosen] = useState<Set<number>>(new Set());

  useEffect(() => {
    seerrApi.tv(m.tmdbId).then((r) => {
      setSeasons(r.seasons);
      setChosen(new Set(r.seasons.filter((s) => s.status === 'none').map((s) => s.number)));
    }).catch(() => setSeasons([]));
  }, [m.tmdbId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const toggle = (n: number) => setChosen((c) => {
    const x = new Set(c);
    if (x.has(n)) x.delete(n);
    else x.add(n);
    return x;
  });

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={`Seizoenen van ${m.title} aanvragen`} onClick={(e) => e.stopPropagation()}>
        <h2>{m.title}</h2>
        <p className="subtle">Kies welke seizoenen je wilt aanvragen.</p>
        {seasons === null && <p className="subtle">Seizoenen laden…</p>}
        <div className="season-list" data-focus-group>
          {seasons?.map((s) => {
            const done = s.status !== 'none';
            return (
              <label key={s.number} className="toggle">
                <input type="checkbox" disabled={done} checked={done || chosen.has(s.number)} onChange={() => toggle(s.number)} data-autofocus={s.number === seasons.find((x) => x.status === 'none')?.number ? '' : undefined} />
                <span>{s.name} <span className="subtle">· {s.episodes} afl.{done ? ` · ${STATUS_LABEL[s.status]}` : ''}</span></span>
              </label>
            );
          })}
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || chosen.size === 0} onClick={() => onConfirm([...chosen].sort((a, b) => a - b))}>
            {busy ? 'Bezig…' : `${chosen.size} seizoen${chosen.size === 1 ? '' : 'en'} aanvragen`}
          </button>
          <button className="btn" onClick={onCancel}>Annuleren</button>
        </div>
      </div>
    </div>
  );
}

/** Kwaliteit kiezen bij een film (bijv. standaard of met Nederlandse audio); het voorstel staat al aan. */
function ProfilePicker({ m, profiles, suggested, busy, onCancel, onConfirm }: { m: SeerrMedia; profiles: { id: number; name: string }[]; suggested?: number; busy: boolean; onCancel: () => void; onConfirm: (id: number) => void }) {
  const [chosen, setChosen] = useState(suggested ?? profiles[0]?.id);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={`${m.title} aanvragen`} onClick={(e) => e.stopPropagation()}>
        <h2>{m.title}</h2>
        <p className="subtle">In welke kwaliteit wil je deze film?</p>
        <div className="season-list" role="radiogroup" data-focus-group>
          {profiles.map((p) => (
            <label key={p.id} className="toggle">
              <input type="radio" name="req-profile" checked={chosen === p.id} onChange={() => setChosen(p.id)} data-autofocus={chosen === p.id ? '' : undefined} />
              <span>{p.name}{p.id === suggested ? <span className="subtle"> · voorgesteld</span> : null}</span>
            </label>
          ))}
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !chosen} onClick={() => chosen && onConfirm(chosen)}>{busy ? 'Bezig…' : 'Aanvragen'}</button>
          <button className="btn" onClick={onCancel}>Annuleren</button>
        </div>
      </div>
    </div>
  );
}
